/**
 * TokenizedInput - Text input with expression syntax highlighting and intellisense
 *
 * Shows expressions inline with highlighting when they match valid patterns.
 * Users can edit the text directly including expressions.
 * Supports drag and drop of tokens from WorkflowInputPanel.
 * Provides autocomplete suggestions when typing inside ${...} expressions.
 */

import { useState, useCallback, useRef, useMemo, useEffect, useContext } from 'react';
import { isPrimaryMod } from '@/utils/input';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
} from '@coreai/fluentui-react';
import { type AgentParameterSchema } from './AgentParameterMenu';
import { wrapAsExpression } from '@/utils/expressions';
import {
  ExpressionAutocomplete,
  methodToSuggestion,
  type AutocompleteSuggestion,
} from './ExpressionAutocomplete';
import type { ValueSegment, Token } from '@/types/valueSegment';
import {
  ValueSegmentType,
  generateSegmentId,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';
import {
  type EvaluationContext,
  stringExtensionsMeta,
  arrayExtensionsMeta,
  numberExtensionsMeta,
  objectExtensionsMeta,
  dateExtensionsMeta,
  inferTypeFromExpression,
} from '@/utils/expressions';
import { evaluateAsync } from '@/utils/expressions/workerEvaluator';
import { getAllExpressionFunctionSuggestions } from '@/data/expressionFunctions';
import { DynamicContentTokensContext } from './dynamicContentTokens';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { ottoTokens } from '@/theme/ottoTokens';
import { useApplyTokenTransforms } from '@/hooks/useApplyTokenTransforms';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  // Fill-parent variants let a host (e.g. `ExpandableEditorDialog`) stretch
  // the editor to fill its container WITHOUT reaching into this component's
  // DOM via structural `& > div > div` selectors. The host opts in with the
  // `fillParent` prop and these stable, component-owned classes do the rest.
  containerFill: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  inputWrapperFill: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    // Row → column so the editor stretches on the block axis to fill height.
    flexDirection: 'column',
  },
  // The editable container with syntax highlighting
  // Padding matches Fluent UI Combobox input for visual consistency
  editor: {
    flex: 1,
    minHeight: '32px',
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalMNudge}`,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorTransparentStroke),
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    outline: 'none',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    cursor: 'text',
    transitionProperty: 'border-color, box-shadow',
    transitionDuration: tokens.durationFaster,
  },
  editorFocused: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
  editorMultiline: {
    minHeight: '80px',
  },
  // Editor half of the fill-parent contract: grow to fill the wrapper and
  // scroll internally past that. Pairs with `containerFill`/`inputWrapperFill`.
  editorFill: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  editorDropTarget: {
    ...shorthands.border('2px', 'dashed', tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  editorDisabled: {
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    color: tokens.colorNeutralForegroundDisabled,
    cursor: 'not-allowed',
  },
  // Placeholder styling - use ::before pseudo-element since contentEditable doesn't work with React children
  placeholder: {
    '&:empty::before': {
      content: 'attr(data-placeholder)',
      color: tokens.colorNeutralForeground4,
      pointerEvents: 'none',
    },
  },
  // Expression highlighting — mirrors the JavaScript variable color used in
  // CodeMirror/code editors so tokens read as "code" wherever they appear.
  expression: {
    backgroundColor: tokens.colorBrandBackground2,
    color: ottoTokens.codeSyntaxVariable,
    borderRadius: tokens.borderRadiusSmall,
    padding: `1px ${tokens.spacingHorizontalXS}`,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
});

/**
 * JavaScript Expression Parser for ${...} syntax
 *
 * Based on the Otto JavaScriptExpressionParser patterns.
 * Handles:
 * - Pure expressions: ${1 + 2}, ${trigger().body}
 * - Interpolations: Hello ${name}!
 * - Nested braces: ${{ a: { b: 1 } }}
 * - String literals: ${"string with } brace"}, ${'single'}, ${`template`}
 * - Escape sequences: \{, \', \\
 * - Escaped $$: Price: $$100 (not an expression)
 */

/**
 * Check if a string contains expression patterns (${...}, @{...}, or @function()).
 */
function containsExpression(text: string): boolean {
  if (!text || text.length < 3) return false;

  let i = 0;
  while (i < text.length - 1) {
    // Check for escaped $$
    if (text.charAt(i) === '$' && text.charAt(i + 1) === '$') {
      i += 2;
      continue;
    }
    // Check for expression start ${
    if (text.charAt(i) === '$' && text.charAt(i + 1) === '{') {
      return true;
    }
    // Check for Logic Apps @{...} expression
    if (text.charAt(i) === '@' && text.charAt(i + 1) === '{') {
      return true;
    }
    // Check for inline @function() expression (e.g., @variables('name'), @body('action'))
    if (text.charAt(i) === '@' && i + 1 < text.length && /[a-zA-Z]/.test(text.charAt(i + 1))) {
      // Look ahead to see if there's a function call pattern
      let j = i + 1;
      while (j < text.length && /[a-zA-Z_]/.test(text.charAt(j))) {
        j++;
      }
      if (j < text.length && text.charAt(j) === '(') {
        return true;
      }
    }
    i++;
  }
  return false;
}

/**
 * Find the end of a ${...} JavaScript expression.
 * Handles:
 * - Balanced braces (nested { and })
 * - String literals (", ', `) - braces inside strings don't count
 * - Escape sequences (\{, \}, \\, \', \", \`)
 *
 * Returns the index after the closing }, or -1 if not found/invalid.
 */
function findJavaScriptExpressionEnd(text: string, start: number): number {
  if (start + 2 >= text.length) return -1;
  if (text.charAt(start) !== '$' || text.charAt(start + 1) !== '{') return -1;

  // Try strict parsing first (respects strings)
  const strictResult = findExpressionEndStrict(text, start);
  if (strictResult !== -1) {
    return strictResult;
  }

  // Fallback: lenient parsing (ignores strings) for better UX while typing
  // This handles cases like unclosed strings: ${foo['bar}
  return findExpressionEndLenient(text, start);
}

/**
 * Strict expression end finder - respects string literals
 */
function findExpressionEndStrict(text: string, start: number): number {
  let pos = start + 2; // Skip "${"
  let braceDepth = 0;
  let inString = false;
  let stringChar = '';
  let escaped = false;

  while (pos < text.length) {
    const char = text.charAt(pos);

    // Handle escape sequences
    if (escaped) {
      escaped = false;
      pos++;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      pos++;
      continue;
    }

    if (!inString) {
      // Check for string start
      if (char === '"' || char === "'" || char === '`') {
        inString = true;
        stringChar = char;
        pos++;
        continue;
      }

      // Track brace depth
      if (char === '{') {
        braceDepth++;
        pos++;
        continue;
      }

      if (char === '}') {
        if (braceDepth === 0) {
          // Found the closing brace of the expression
          return pos + 1; // Return position after }
        }
        braceDepth--;
        pos++;
        continue;
      }
    } else {
      // Inside a string - check for string end
      if (char === stringChar) {
        inString = false;
        stringChar = '';
      }
    }

    pos++;
  }

  // Reached end of text without finding closing brace
  return -1;
}

/**
 * Lenient expression end finder - ignores string literals
 * Used as fallback when strict parsing fails (e.g., unclosed strings)
 * This provides better UX while the user is typing
 */
function findExpressionEndLenient(text: string, start: number): number {
  let pos = start + 2; // Skip "${"
  let braceDepth = 0;

  while (pos < text.length) {
    const char = text.charAt(pos);

    if (char === '{') {
      braceDepth++;
    } else if (char === '}') {
      if (braceDepth === 0) {
        // Found the closing brace of the expression
        return pos + 1;
      }
      braceDepth--;
    }

    pos++;
  }

  return -1;
}

/**
 * Validate that the content inside a ${...} expression looks like valid JavaScript.
 * This is a basic validation to avoid highlighting obviously invalid expressions.
 *
 * Valid expressions should contain:
 * - Function calls: trigger(), body('action'), etc.
 * - Property access: obj.prop, obj['prop']
 * - Operators and values: 1 + 2, "string", true, null
 * - Object/array literals: { a: 1 }, [1, 2, 3]
 * - Arrow functions: x => x * 2
 *
 * Invalid (should not highlight):
 * - Empty: ${}
 * - Only whitespace: ${   }
 */
function isValidExpressionContent(content: string): boolean {
  if (!content) return false;

  const trimmed = content.trim();
  if (trimmed.length === 0) return false;

  // Expression has some content - consider it valid
  // Full JavaScript validation would require a proper parser
  return true;
}

/**
 * Parse text and return segments with JavaScript expressions marked.
 *
 * Handles:
 * - ${...} JavaScript expressions
 * - Escaped $$ sequences (not highlighted)
 * - String literals inside expressions
 * - Nested braces inside expressions
 */
export function parseExpressions(text: string): Array<{ text: string; isExpression: boolean }> {
  if (!text) return [];

  const result: Array<{ text: string; isExpression: boolean }> = [];
  let pos = 0;
  let lastEnd = 0;

  while (pos < text.length) {
    // Check for escaped $$ - skip it (don't treat as expression)
    if (pos < text.length - 1 && text.charAt(pos) === '$' && text.charAt(pos + 1) === '$') {
      pos += 2;
      continue;
    }

    // Check for expression start ${
    if (pos < text.length - 1 && text.charAt(pos) === '$' && text.charAt(pos + 1) === '{') {
      const end = findJavaScriptExpressionEnd(text, pos);

      if (end > 0) {
        // Extract the content between ${ and }
        const expressionContent = text.substring(pos + 2, end - 1);

        // Only highlight if the expression content is valid
        if (isValidExpressionContent(expressionContent)) {
          // Add text before expression
          if (pos > lastEnd) {
            result.push({ text: text.substring(lastEnd, pos), isExpression: false });
          }
          // Add expression
          result.push({ text: text.substring(pos, end), isExpression: true });
          lastEnd = end;
          pos = end;
          continue;
        }
      }
    }

    // Check for Logic Apps @{...} expression format
    if (pos < text.length - 1 && text.charAt(pos) === '@' && text.charAt(pos + 1) === '{') {
      const end = findLogicAppsExpressionEnd(text, pos + 1); // Start after @

      if (end > 0) {
        // Add text before expression
        if (pos > lastEnd) {
          result.push({ text: text.substring(lastEnd, pos), isExpression: false });
        }
        // Add expression (including the @)
        result.push({ text: text.substring(pos, end), isExpression: true });
        lastEnd = end;
        pos = end;
        continue;
      }
    }

    // Check for Logic Apps inline @function(...) expression format
    // e.g., @variables('name'), @body('action'), @outputs('action')
    if (text.charAt(pos) === '@' && pos + 1 < text.length && /[a-zA-Z]/.test(text.charAt(pos + 1))) {
      const end = findInlineExpressionEnd(text, pos);

      if (end > pos) {
        // Add text before expression
        if (pos > lastEnd) {
          result.push({ text: text.substring(lastEnd, pos), isExpression: false });
        }
        // Add expression
        result.push({ text: text.substring(pos, end), isExpression: true });
        lastEnd = end;
        pos = end;
        continue;
      }
    }

    pos++;
  }

  // Add remaining text
  if (lastEnd < text.length) {
    result.push({ text: text.substring(lastEnd), isExpression: false });
  }

  return result;
}

/**
 * Find the end of a Logic Apps @{...} expression
 */
function findLogicAppsExpressionEnd(text: string, startPos: number): number {
  if (text.charAt(startPos) !== '{') return -1;

  let depth = 1;
  let pos = startPos + 1;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar) {
        // Count consecutive backslashes before this quote
        let backslashCount = 0;
        let k = pos - 1;
        while (k >= 0 && text.charAt(k) === '\\') {
          backslashCount++;
          k--;
        }
        // Quote is escaped only if preceded by odd number of backslashes
        if (backslashCount % 2 === 0) {
          inString = false;
        }
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos : -1;
}

/**
 * Find the end of a Logic Apps inline @function(...) expression
 * Handles nested parentheses and string literals
 */
function findInlineExpressionEnd(text: string, startPos: number): number {
  if (text.charAt(startPos) !== '@') return -1;

  let pos = startPos + 1;

  // Skip function name (letters and underscores)
  while (pos < text.length && /[a-zA-Z_]/.test(text.charAt(pos))) {
    pos++;
  }

  // Must have opening parenthesis
  if (pos >= text.length || text.charAt(pos) !== '(') {
    return -1;
  }

  // Find matching closing parenthesis
  let depth = 1;
  pos++; // Skip opening paren
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar) {
        // Count consecutive backslashes before this quote
        let backslashCount = 0;
        let k = pos - 1;
        while (k >= 0 && text.charAt(k) === '\\') {
          backslashCount++;
          k--;
        }
        // Quote is escaped only if preceded by odd number of backslashes
        if (backslashCount % 2 === 0) {
          inString = false;
        }
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '(') {
        depth++;
      } else if (char === ')') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos : -1;
}

/**
 * Convert string to ValueSegment array
 */
function stringToSegments(value: string): ValueSegment[] {
  if (!value) {
    return [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: '',
    }];
  }

  // Just store as a single literal - we highlight expressions visually but don't segment
  return [{
    id: generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value,
  }];
}

/**
 * Expression context for intellisense
 */
export interface ExpressionContext {
  /** Whether we're inside a ${...} expression */
  isInExpression: boolean;
  /** The start position of the current expression (position of $) */
  expressionStart: number;
  /** Content inside the expression before cursor */
  expressionContent: string;
  /** Whether cursor is after a dot (for method suggestions) */
  isAfterDot: boolean;
  /** The expression before the dot */
  expressionBeforeDot: string;
  /** Filter text after the dot */
  filterTextAfterDot: string;
  /** Inferred type of expression before dot */
  inferredType: string;
  /** The cursor position when this context was captured */
  cursorPosition: number;
}

/**
 * Extract the expression chain ending at a given position.
 * This handles cases like:
 * - `triggerBody()` → `triggerBody()`
 * - `const i = triggerBody()` → `triggerBody()`
 * - `foo.bar.baz` → `foo.bar.baz`
 * - `arr[0].prop` → `arr[0].prop`
 */
export function extractExpressionChain(content: string, endPos: number): string {
  if (endPos <= 0) return '';

  let pos = endPos - 1;

  // Skip backwards through the expression chain
  // An expression chain can include: identifiers, function calls (), property access [], dots
  while (pos >= 0) {
    const char = content.charAt(pos);

    // Skip whitespace between parts (e.g., `foo .bar` though unusual)
    if (/\s/.test(char)) {
      pos--;
      continue;
    }

    // Handle closing parenthesis - find matching open
    if (char === ')') {
      let depth = 1;
      pos--;
      while (pos >= 0 && depth > 0) {
        const c = content.charAt(pos);
        if (c === ')') depth++;
        else if (c === '(') depth--;
        // Handle strings inside parens
        else if ((c === '"' || c === "'" || c === '`') && pos > 0) {
          const quote = c;
          pos--;
          while (pos >= 0 && content.charAt(pos) !== quote) {
            if (content.charAt(pos) === '\\' && pos > 0) pos--; // Skip escaped chars
            pos--;
          }
        }
        pos--;
      }
      continue;
    }

    // Handle closing bracket - find matching open
    if (char === ']') {
      let depth = 1;
      pos--;
      while (pos >= 0 && depth > 0) {
        const c = content.charAt(pos);
        if (c === ']') depth++;
        else if (c === '[') depth--;
        // Handle strings inside brackets
        else if ((c === '"' || c === "'" || c === '`') && pos > 0) {
          const quote = c;
          pos--;
          while (pos >= 0 && content.charAt(pos) !== quote) {
            if (content.charAt(pos) === '\\' && pos > 0) pos--;
            pos--;
          }
        }
        pos--;
      }
      continue;
    }

    // Handle dot - continue the chain
    if (char === '.') {
      pos--;
      continue;
    }

    // Handle identifier characters
    if (/[a-zA-Z0-9_$]/.test(char)) {
      // Skip the entire identifier
      while (pos >= 0 && /[a-zA-Z0-9_$]/.test(content.charAt(pos))) {
        pos--;
      }
      continue;
    }

    // Any other character breaks the chain
    break;
  }

  // Extract from pos+1 to endPos
  return content.substring(pos + 1, endPos).trim();
}

/**
 * Analyze cursor context to determine intellisense state
 */
export function analyzeExpressionContext(text: string, cursorPosition: number): ExpressionContext {
  const result: ExpressionContext = {
    isInExpression: false,
    expressionStart: -1,
    expressionContent: '',
    isAfterDot: false,
    expressionBeforeDot: '',
    filterTextAfterDot: '',
    inferredType: 'any',
    cursorPosition, // Store the cursor position for later use
  };

  if (!text || cursorPosition < 0) return result;

  // Find if cursor is inside a ${...} or @{...} expression
  const exprPrefixLength = 2; // Both ${ and @{ are 2 characters
  let pos = 0;
  let currentExprStart = -1;

  while (pos < text.length && pos < cursorPosition) {
    // Check for escaped $$
    if (pos < text.length - 1 && text.charAt(pos) === '$' && text.charAt(pos + 1) === '$') {
      pos += 2;
      continue;
    }

    // Check for expression start ${
    if (pos < text.length - 1 && text.charAt(pos) === '$' && text.charAt(pos + 1) === '{') {
      const end = findJavaScriptExpressionEnd(text, pos);

      if (end === -1 || end > cursorPosition) {
        currentExprStart = pos;
        break;
      }

      pos = end;
      continue;
    }

    // Check for Logic Apps expression start @{
    if (pos < text.length - 1 && text.charAt(pos) === '@' && text.charAt(pos + 1) === '{') {
      const end = findLogicAppsExpressionEnd(text, pos + 1); // Start after @

      if (end === -1 || end > cursorPosition) {
        currentExprStart = pos;
        break;
      }

      pos = end;
      continue;
    }

    pos++;
  }

  if (currentExprStart === -1) {
    return result;
  }

  result.isInExpression = true;
  result.expressionStart = currentExprStart;
  result.expressionContent = text.substring(currentExprStart + exprPrefixLength, cursorPosition);

  // Check if cursor is after a dot
  const contentBeforeCursor = result.expressionContent;
  const lastDotIndex = contentBeforeCursor.lastIndexOf('.');

  if (lastDotIndex >= 0) {
    // Check that the dot isn't inside a string
    const afterDot = contentBeforeCursor.substring(lastDotIndex + 1);

    // Only consider it a method access if there's no quote after the dot
    if (!/["'`]/.test(afterDot)) {
      // Extract just the expression chain before the dot, not all the code
      const expressionChain = extractExpressionChain(contentBeforeCursor, lastDotIndex);

      if (expressionChain) {
        // Check if this looks like a valid expression (not inside a string)
        let inString = false;
        let stringChar = '';
        for (let i = 0; i < expressionChain.length; i++) {
          const c = expressionChain.charAt(i);
          if (!inString && (c === '"' || c === "'" || c === '`')) {
            inString = true;
            stringChar = c;
          } else if (inString && c === stringChar && expressionChain.charAt(i - 1) !== '\\') {
            inString = false;
          }
        }

        if (!inString) {
          result.isAfterDot = true;
          result.expressionBeforeDot = expressionChain;
          result.filterTextAfterDot = afterDot;
          result.inferredType = inferTypeFromExpression(expressionChain);
        }
      }
    }
  }

  return result;
}

/**
 * Get a human-readable type string for a value
 */
export function getTypeString(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'array';
    const itemType = getTypeString(value[0]);
    return `${itemType}[]`;
  }
  if (value instanceof Date) return 'Date';
  if (typeof value === 'object') return 'object';
  return 'any';
}

/**
 * Format a value for preview display (truncated)
 */
export function formatPreviewValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') {
    return value.length > 50 ? `"${value.slice(0, 50)}..."` : `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `Array(${value.length})`;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', ...' : ''}}`;
  }
  return String(value);
}

/**
 * Infer the type of an expression by evaluating it against run data (async, worker-isolated)
 */
export async function inferTypeFromRunData(
  expressionBeforeDot: string,
  actionOutputs?: Record<string, unknown>
): Promise<{ type: string; value: unknown }> {
  if (!actionOutputs) {
    return { type: inferTypeFromExpression(expressionBeforeDot), value: undefined };
  }

  const variables = (actionOutputs['__variables__'] ?? {}) as Record<string, unknown>;
  const evalContext: EvaluationContext = {
    trigger: actionOutputs['trigger'],
    actionOutputs: actionOutputs,
    variables,
  };

  const wrappedExpr = `\${${expressionBeforeDot}}`;
  const result = await evaluateAsync(wrappedExpr, evalContext);

  if (!result.success || result.value === undefined) {
    return { type: inferTypeFromExpression(expressionBeforeDot), value: undefined };
  }

  const value = result.value;
  if (typeof value === 'string') return { type: 'string', value };
  if (typeof value === 'number') return { type: 'number', value };
  if (typeof value === 'boolean') return { type: 'boolean', value };
  if (Array.isArray(value)) return { type: 'array', value };
  if (value instanceof Date) return { type: 'Date', value };
  if (typeof value === 'object' && value !== null) return { type: 'object', value };

  return { type: 'any', value };
}

/**
 * Result from generating suggestions
 */
interface SuggestionResult {
  suggestions: AutocompleteSuggestion[];
  inferredType: string;
}

/**
 * Generate autocomplete suggestions based on context and run data
 */
export async function generateSuggestions(
  context: ExpressionContext,
  actionOutputs?: Record<string, unknown>,
  dynamicContentSuggestions?: AutocompleteSuggestion[]
): Promise<SuggestionResult> {
  const suggestions: AutocompleteSuggestion[] = [];
  let inferredType = 'any';

  if (!context.isInExpression) {
    return { suggestions, inferredType };
  }

  if (context.isAfterDot) {
    // Evaluate the expression before the dot to get the actual runtime type AND value
    const { type, value } = await inferTypeFromRunData(context.expressionBeforeDot, actionOutputs);
    inferredType = type;

    // If it's an object, show its actual properties first
    if (type === 'object' && value && typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj);

      // Add actual properties from the object
      for (const key of keys) {
        const propValue = obj[key];
        const propType = getTypeString(propValue);
        suggestions.push({
          label: key,
          type: 'property',
          detail: `→ ${propType}`,
          documentation: `Property from evaluated data. Value: ${formatPreviewValue(propValue)}`,
          insertText: key,
          category: 'Properties',
          returnType: propType,
        });
      }
    }

    // If it's an array with items, show index access and item properties
    if (type === 'array' && Array.isArray(value) && value.length > 0) {
      // Suggest array index access
      suggestions.push({
        label: '[0]',
        type: 'property',
        detail: '→ first item',
        documentation: 'Access the first element of the array',
        insertText: '[0]',
        category: 'Array Access',
        returnType: getTypeString(value[0]),
      });

      // If array items are objects, show their common properties
      const firstItem = value[0];
      if (firstItem && typeof firstItem === 'object' && !Array.isArray(firstItem)) {
        const itemKeys = Object.keys(firstItem as Record<string, unknown>);
        for (const key of itemKeys.slice(0, 10)) { // Limit to 10 properties
          suggestions.push({
            label: `[0].${key}`,
            type: 'property',
            detail: `→ ${getTypeString((firstItem as Record<string, unknown>)[key])}`,
            documentation: `Access "${key}" property of first array item`,
            insertText: `[0].${key}`,
            category: 'Array Item Properties',
            returnType: getTypeString((firstItem as Record<string, unknown>)[key]),
          });
        }
      }
    }

    // Add type-specific methods
    // Initialize as undefined - only set for known types
    let extensionMeta: typeof stringExtensionsMeta | undefined = undefined;
    let categoryName = 'Methods';

    switch (type) {
      case 'string':
        extensionMeta = stringExtensionsMeta;
        categoryName = 'String Methods';
        break;
      case 'array':
        extensionMeta = arrayExtensionsMeta;
        categoryName = 'Array Methods';
        break;
      case 'number':
        extensionMeta = numberExtensionsMeta;
        categoryName = 'Number Methods';
        break;
      case 'object':
        extensionMeta = objectExtensionsMeta;
        categoryName = 'Object Methods';
        break;
      case 'Date':
      case 'DateTime':
        extensionMeta = dateExtensionsMeta;
        categoryName = 'Date Methods';
        break;
      default:
        // Unknown type ('any' or unrecognized) - show helpful suggestions
        // Don't show type-specific methods as that would be misleading
        suggestions.push({
          label: '[0]',
          type: 'property',
          detail: 'Array access',
          documentation: 'Access array element by index (if this is an array)',
          insertText: '[0]',
          category: 'Common Patterns',
          returnType: 'any',
        });
        suggestions.push({
          label: 'propertyName',
          type: 'property',
          detail: 'Object property',
          documentation: 'Access object property (type the actual property name)',
          insertText: '',
          category: 'Common Patterns',
          returnType: 'any',
        });
        // Show a few useful methods that work on multiple types
        suggestions.push({
          label: 'toString()',
          type: 'method',
          detail: 'Convert to string',
          documentation: 'Convert value to string representation',
          insertText: 'toString()',
          category: 'Common Methods',
          returnType: 'string',
        });
        // extensionMeta stays undefined, so no type-specific methods will be added
        break;
    }

    // Only add extension methods if we have a known type (extensionMeta is defined)
    if (extensionMeta) {
      for (const method of extensionMeta.methods) {
        suggestions.push(methodToSuggestion(method, categoryName));
      }
    }
  } else if (context.expressionContent.trim() === '' || context.expressionContent.trim() === '$') {
    // At the start of expression — surface dynamic-content tokens first (the
    // same set the token picker rail and field-fork popover show), then the
    // curated function library. Tokens are the most common insert, so they
    // lead; both are filtered together by what the user types next.
    if (dynamicContentSuggestions?.length) {
      for (const suggestion of dynamicContentSuggestions) {
        suggestions.push(suggestion);
      }
    }
    // Sourced from `data/expressionFunctions.ts`; the `Workflow` / `Global
    // Functions` grouping matches FunctionPickerPanel.
    for (const suggestion of getAllExpressionFunctionSuggestions()) {
      suggestions.push(suggestion);
    }
  }

  // Filter suggestions based on what user has typed
  const filter = context.isAfterDot
    ? context.filterTextAfterDot.toLowerCase()
    : context.expressionContent.toLowerCase().replace(/^\$?/, '');

  if (filter) {
    const filteredSuggestions = suggestions.filter((s) =>
      s.label.toLowerCase().startsWith(filter) ||
      s.label.toLowerCase().includes(filter)
    );
    return { suggestions: filteredSuggestions, inferredType };
  }

  return { suggestions, inferredType };
}

export interface TokenizedInputProps {
  /** Field name/id */
  name: string;
  /** Current value as segments or string */
  value: ValueSegment[] | string;
  /** Called when value changes */
  onChange: (segments: ValueSegment[]) => void;
  /** Placeholder text */
  placeholder?: string | undefined;
  /**
   * Accessible name for the textbox. Falls back to the placeholder when not
   * provided. A placeholder alone does not satisfy WCAG (aria-input-field-name),
   * so callers without visible labels should set this explicitly.
   */
  ariaLabel?: string | undefined;
  /** Whether this is a multiline input */
  multiline?: boolean | undefined;
  /** Whether the input is disabled */
  disabled?: boolean | undefined;
  /** Whether the input is read-only */
  readonly?: boolean | undefined;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name. Used for autocomplete type inference. */
  actionOutputs?: Record<string, unknown> | undefined;
  /**
   * Optional CSS max-height for the editor. Only takes effect in `multiline`
   * mode. When set, the editor stops growing at this value and an internal
   * vertical scrollbar takes over. Accepts any valid CSS length or clamp()
   * expression, e.g. `'240px'`, `'clamp(120px, 20vh, 200px)'`. When unset
   * (default), the editor grows with content — legacy behavior.
   */
  maxHeight?: string | number | undefined;
  /**
   * When true, the editor stretches to fill its parent's height and scrolls
   * internally past that, instead of growing with content. Lets a host size
   * the editor via a stable prop rather than coupling to this component's
   * internal DOM structure. Intended for full-height containers like
   * `ExpandableEditorDialog`. Best paired with `multiline`.
   */
  fillParent?: boolean | undefined;
}

/**
 * TokenizedInput - Text input with expression syntax highlighting, intellisense, and drag-drop support
 */
export const TokenizedInput = ({
  name,
  value,
  onChange,
  placeholder,
  ariaLabel,
  multiline = false,
  disabled = false,
  readonly = false,
  autoFocus = false,
  // showAgentParameterButton and agentParameterSchema kept for API compatibility
  // (field components still pass them). Agent param button moved to FieldActionChips.
  showAgentParameterButton: _showAgentParam = false,
  agentParameterSchema: _agentSchema,
  actionOutputs,
  maxHeight,
  fillParent = false,
}: TokenizedInputProps) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Honor read-only mode from the node-editor store. <fieldset disabled>
  // doesn't propagate to contentEditable divs, so we have to read it ourselves.
  const isStoreReadOnly = useNodeEditorStore((s) => s.isReadOnly);
  const effectiveReadonly = readonly || isStoreReadOnly;
  const applyTokenTransforms = useApplyTokenTransforms();

  // Dynamic-content suggestions published by the node editor panel (flattened
  // once per panel). The inline `${…}` dropdown offers the same tokens as the
  // picker rail / field-fork popover. Empty when this input is
  // rendered outside a panel that provides the context.
  const dynamicContentSuggestions = useContext(DynamicContentTokensContext);

  // Auto-focus on mount, placing the caret at the end of any existing content.
  // Deferred via rAF so the content-rendering effect (which writes innerHTML)
  // has run before we position the selection.
  useEffect(() => {
    if (!autoFocus || disabled || effectiveReadonly) return;
    const raf = requestAnimationFrame(() => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const selection = window.getSelection();
      if (!selection) return;
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFocus, disabled, effectiveReadonly]);
  // Track cursor position for inserting tokens from menus
  const lastCursorPositionRef = useRef<number | null>(null);
  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [autocompleteFilterText, setAutocompleteFilterText] = useState('');
  const [autocompleteContext, setAutocompleteContext] = useState<ExpressionContext | null>(null);
  const [autocompleteInferredType, setAutocompleteInferredType] = useState<string>('any');
  // Track if autocomplete is being interacted with
  const isInteractingWithAutocompleteRef = useRef(false);
  // Save autocomplete context in ref to persist across click events
  const autocompleteContextRef = useRef<ExpressionContext | null>(null);

  // Get the raw text value
  const textValue = useMemo(() => {
    if (isValueSegmentArray(value)) {
      return segmentsToString(value);
    }
    return typeof value === 'string' ? value : '';
  }, [value]);

  // Parse expressions for highlighting
  const parsedSegments = useMemo(() => {
    return parseExpressions(textValue);
  }, [textValue]);

  // Check if we have any expressions
  const hasExpressions = useMemo(() => {
    return containsExpression(textValue);
  }, [textValue]);

  // Check if empty
  const isEmpty = !textValue;

  // Refs/state for the internal undo stack are declared here; the effect
  // and apply/undo/redo callbacks are defined after restoreCursorPosition
  // (which they depend on) further below.
  type UndoSnap = { segments: ValueSegment[]; text: string };
  const undoStackRef = useRef<UndoSnap[]>([]);
  const redoStackRef = useRef<UndoSnap[]>([]);
  const prevSnapRef = useRef<UndoSnap>({
    segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
    text: textValue,
  });
  const lastChangeAtRef = useRef<number>(0);
  // Set to true when the next `value` prop change comes from our own
  // undo/redo apply; the effect skips that change to avoid corrupting the
  // stacks with the very state we just restored.
  const suppressNextValueEffectRef = useRef<boolean>(false);
  // Set to true immediately before any user-input path calls onChange
  // (handleInput, paste, autocomplete insert, auto-pair, etc.). The next
  // value-prop change is then treated as a user edit and pushes a snapshot.
  // Any value-prop change WITHOUT this flag set is an external change
  // (workflow-level undo, parent normalization, switching to a different
  // parameter, hot-reload) and must reset the local stack — keeping stale
  // local snapshots would let a later Ctrl/Cmd+Z restore unrelated state.
  const userEditPendingRef = useRef<boolean>(false);

  // Parameter identity: when this changes we're being remounted onto a
  // different field (e.g. selecting another action or another parameter
  // of the same action). Reset the local stacks unconditionally — same-
  // text values across two different fields must not share undo history.
  // We track this in a ref synchronously so the value-effect below sees
  // the reset before its same-text early return runs.
  const prevNameRef = useRef<string>(name);
  if (prevNameRef.current !== name) {
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastChangeAtRef.current = 0;
    userEditPendingRef.current = false;
    suppressNextValueEffectRef.current = false;
    prevSnapRef.current = {
      segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
      text: textValue,
    };
    prevNameRef.current = name;
  }

  /**
   * Wrap `onChange` for any user-initiated edit so the next `value`-prop
   * round-trip is recorded by the local undo stack. All user-input
   * paths in this component MUST use this instead of calling `onChange`
   * directly.
   */
  const commitUserEdit = useCallback(
    (next: ValueSegment[]) => {
      userEditPendingRef.current = true;
      onChange(next);
    },
    [onChange],
  );

  /**
   * Get the text content of a node, counting <br> and block elements as newlines.
   * This matches how innerText calculates text content.
   */
  const getNodeTextLength = useCallback((node: Node, isLastChild: boolean = false): number => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent?.length ?? 0;
    }
    if (node.nodeName === 'BR') {
      return 1; // <br> counts as 1 character (newline)
    }

    // Block elements (div, p) add a newline after their content (except if last child)
    const isBlockElement = node.nodeName === 'DIV' || node.nodeName === 'P';

    // For element nodes, sum up children
    let length = 0;
    const children = Array.from(node.childNodes);
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child) {
        const isLast = i === children.length - 1;
        length += getNodeTextLength(child, isLast);
      }
    }

    // Add newline for block elements (but not if it's the last child to avoid trailing newline)
    if (isBlockElement && !isLastChild && length > 0) {
      length += 1;
    }

    return length;
  }, []);

  /**
   * Restore cursor position based on character offset.
   * Handles text nodes, <br> elements, and nested structures.
   */
  const restoreCursorPosition = useCallback((offset: number | null) => {
    if (offset === null || !editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    // Recursive function to find the position
    const findPosition = (node: Node, remainingOffset: number): { node: Node; offset: number } | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        const len = node.textContent?.length ?? 0;
        if (remainingOffset <= len) {
          return { node, offset: remainingOffset };
        }
        return null;
      }

      if (node.nodeName === 'BR') {
        const parent = node.parentNode;
        if (!parent) return null;
        const siblings = Array.from(parent.childNodes);
        const nodeIndex = siblings.indexOf(node as ChildNode);

        if (remainingOffset === 0) {
          // Position before the BR
          return { node: parent, offset: nodeIndex };
        }
        if (remainingOffset === 1) {
          // Position after the BR - check if there's a next sibling
          const nextSibling = siblings[nodeIndex + 1];
          if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
            // Position at start of next text node
            return { node: nextSibling, offset: 0 };
          }
          // Position after BR in parent
          return { node: parent, offset: nodeIndex + 1 };
        }
        return null;
      }

      // Element node - traverse children
      let currentOffset = 0;
      const children = Array.from(node.childNodes);

      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (!child) continue;
        const childLength = getNodeTextLength(child);

        if (currentOffset + childLength >= remainingOffset) {
          const result = findPosition(child, remainingOffset - currentOffset);
          if (result) return result;
          // If we couldn't find it in the child, position after the child
          return { node, offset: i + 1 };
        }
        currentOffset += childLength;
      }

      // Position at the end of this node
      return { node, offset: children.length };
    };

    const result = findPosition(editorRef.current, offset);
    if (result) {
      const range = document.createRange();
      try {
        range.setStart(result.node, result.offset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch {
        // Fallback: move to end
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [getNodeTextLength]);

  /**
   * Save cursor position relative to the text content.
   * Returns the character offset from the start of the editor.
   */
  const saveCursorPosition = useCallback((): number | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return null;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.startContainer)) return null;

    // Calculate offset by counting characters from start to cursor
    const countOffset = (node: Node, targetNode: Node, targetOffset: number): number | null => {
      if (node === targetNode) {
        if (node.nodeType === Node.TEXT_NODE) {
          return targetOffset;
        }
        // For element nodes, targetOffset is the child index
        let count = 0;
        const children = Array.from(node.childNodes);
        for (let i = 0; i < targetOffset && i < children.length; i++) {
          const child = children[i];
          if (child) {
            count += getNodeTextLength(child);
          }
        }
        return count;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        return null; // Not found in this node
      }

      if (node.nodeName === 'BR') {
        return null; // Not found
      }

      // Traverse children
      let offset = 0;
      for (const child of Array.from(node.childNodes)) {
        if (child.contains(targetNode) || child === targetNode) {
          const childOffset = countOffset(child, targetNode, targetOffset);
          if (childOffset !== null) {
            return offset + childOffset;
          }
        }
        offset += getNodeTextLength(child);
      }

      return null;
    };

    const result = countOffset(editorRef.current, range.startContainer, range.startOffset);
    return result;
  }, [getNodeTextLength]);

  // ─── Internal undo / redo stack ────────────────────────────────────────────
  //
  // TokenizedInput is a contentEditable that re-renders on every change,
  // which destroys the browser's native undo buffer. We maintain our own
  // snapshot stack of segment arrays with Lexical-style burst coalescing
  // (~500ms) so Ctrl/Cmd+Z restores the previous typing-burst state. 
  const UNDO_BURST_MS = 500;
  const UNDO_STACK_LIMIT = 100;

  useEffect(() => {
    if (textValue === prevSnapRef.current.text) {
      // Same-text update (e.g. blur re-running handleInput, or any
      // path that produces a new segments array with unchanged
      // textValue). Consume any pending flags so they don't bleed
      // into a subsequent external value change and misclassify it.
      userEditPendingRef.current = false;
      suppressNextValueEffectRef.current = false;
      prevSnapRef.current = {
        segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
        text: textValue,
      };
      return;
    }
    if (suppressNextValueEffectRef.current) {
      suppressNextValueEffectRef.current = false;
      prevSnapRef.current = {
        segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
        text: textValue,
      };
      return;
    }
    if (userEditPendingRef.current) {
      // User-initiated edit — push onto the undo stack with burst
      // coalescing. Within UNDO_BURST_MS of the previous push we
      // coalesce; outside that window we record a new step.
      userEditPendingRef.current = false;
      const now = Date.now();
      if (now - lastChangeAtRef.current > UNDO_BURST_MS) {
        undoStackRef.current.push(prevSnapRef.current);
        if (undoStackRef.current.length > UNDO_STACK_LIMIT) {
          undoStackRef.current.shift();
        }
        redoStackRef.current = [];
      }
      lastChangeAtRef.current = now;
      prevSnapRef.current = {
        segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
        text: textValue,
      };
      return;
    }
    // External value change (workflow-level undo, parent normalization,
    // switching to a different parameter, hot-reload). Reset the local
    // stack: keeping old snapshots would let a later Ctrl/Cmd+Z restore
    // unrelated state from the previous context.
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastChangeAtRef.current = 0;
    prevSnapRef.current = {
      segments: isValueSegmentArray(value) ? value : stringToSegments(textValue),
      text: textValue,
    };
  }, [value, textValue]);

  const applySnapshot = useCallback(
    (snap: UndoSnap) => {
      suppressNextValueEffectRef.current = true;
      onChange(snap.segments);
      requestAnimationFrame(() => {
        restoreCursorPosition(snap.text.length);
      });
    },
    [onChange, restoreCursorPosition],
  );

  const performUndo = useCallback((): boolean => {
    const snap = undoStackRef.current.pop();
    if (!snap) return false;
    redoStackRef.current.push(prevSnapRef.current);
    if (redoStackRef.current.length > UNDO_STACK_LIMIT) {
      redoStackRef.current.shift();
    }
    applySnapshot(snap);
    // Force the next change to start a fresh burst so it isn't coalesced
    // into the just-undone snapshot.
    lastChangeAtRef.current = 0;
    return true;
  }, [applySnapshot]);

  const performRedo = useCallback((): boolean => {
    const snap = redoStackRef.current.pop();
    if (!snap) return false;
    undoStackRef.current.push(prevSnapRef.current);
    if (undoStackRef.current.length > UNDO_STACK_LIMIT) {
      undoStackRef.current.shift();
    }
    applySnapshot(snap);
    lastChangeAtRef.current = 0;
    return true;
  }, [applySnapshot]);

  /**
   * Get the pixel position of the cursor for autocomplete positioning
   * Returns viewport-relative coordinates for use with position: fixed
   */
  const getCursorPixelPosition = useCallback((): { top: number; left: number } | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    // Create a temporary span to measure position
    const span = document.createElement('span');
    span.appendChild(document.createTextNode('\u200B')); // Zero-width space
    range.insertNode(span);

    const rect = span.getBoundingClientRect();

    // Clean up
    const parent = span.parentNode;
    if (parent) {
      parent.removeChild(span);
      parent.normalize();
    }

    // Return viewport-relative coordinates (for position: fixed)
    return {
      top: rect.bottom,
      left: rect.left,
    };
  }, []);

  /**
   * Update autocomplete based on current cursor position
   */
  const updateAutocomplete = useCallback(async () => {
    if (!editorRef.current || disabled || effectiveReadonly) {
      setShowAutocomplete(false);
      return;
    }

    const cursorPos = saveCursorPosition();
    if (cursorPos === null) {
      setShowAutocomplete(false);
      return;
    }

    // Read current text directly from DOM to get the latest content
    // (React state may not have updated yet after keypress)
    const currentText = editorRef.current.innerText || '';

    const context = analyzeExpressionContext(currentText, cursorPos);
    setAutocompleteContext(context);
    autocompleteContextRef.current = context;

    if (!context.isInExpression) {
      setShowAutocomplete(false);
      return;
    }

    // Generate suggestions using actual run data for type inference
    const { suggestions, inferredType } = await generateSuggestions(context, actionOutputs, dynamicContentSuggestions);

    if (suggestions.length === 0) {
      setShowAutocomplete(false);
      return;
    }

    // Get cursor position for dropdown
    const pixelPos = getCursorPixelPosition();
    if (!pixelPos) {
      setShowAutocomplete(false);
      return;
    }

    // Adjust position to ensure dropdown stays within viewport
    // Dropdown dimensions: 600px wide, 400px tall
    const DROPDOWN_WIDTH = 600;
    const DROPDOWN_HEIGHT = 400;
    const PADDING = 10; // Padding from viewport edges

    let finalTop = pixelPos.top;
    let finalLeft = pixelPos.left;

    // Check if dropdown would go off the right edge
    if (finalLeft + DROPDOWN_WIDTH > window.innerWidth - PADDING) {
      finalLeft = Math.max(PADDING, window.innerWidth - DROPDOWN_WIDTH - PADDING);
    }

    // Check if dropdown would go off the bottom edge
    if (finalTop + DROPDOWN_HEIGHT > window.innerHeight - PADDING) {
      // Position above the cursor instead
      const cursorHeight = 20; // Approximate cursor/line height
      finalTop = Math.max(PADDING, pixelPos.top - DROPDOWN_HEIGHT - cursorHeight);
    }

    setAutocompleteSuggestions(suggestions);
    setAutocompleteInferredType(inferredType);
    setAutocompletePosition({ top: finalTop, left: finalLeft });
    setAutocompleteFilterText(context.isAfterDot ? context.filterTextAfterDot : '');
    setSelectedSuggestionIndex(0);
    setShowAutocomplete(true);
  }, [disabled, effectiveReadonly, saveCursorPosition, getCursorPixelPosition, actionOutputs, dynamicContentSuggestions]);

  // Token sources resolve asynchronously (upstream manifests, run data). If the
  // user opened the `${…}` dropdown before they arrived, refresh it once they
  // do so the newly available tokens appear without another keystroke.
  useEffect(() => {
    if (showAutocomplete) updateAutocomplete();
    // Only react to suggestions changing; updateAutocomplete/showAutocomplete
    // are intentionally omitted to avoid re-running on unrelated state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicContentSuggestions]);

  /**
   * Handle autocomplete selection
   */
  const handleAutocompleteSelect = useCallback((suggestion: AutocompleteSuggestion) => {
    // Use ref for context since it persists across the click event
    const context = autocompleteContextRef.current;
    if (!editorRef.current || !context) return;

    // Use the cursor position that was saved when autocomplete was triggered
    // This ensures we insert at the correct position even if focus has changed
    const cursorPos = context.cursorPosition;

    const insertText = suggestion.insertText;
    let newCursorOffset = 0;

    if (context.isAfterDot) {
      // Replace the filter text after the dot
      const filterLength = context.filterTextAfterDot.length;
      const beforeFilter = textValue.substring(0, cursorPos - filterLength);
      const afterCursor = textValue.substring(cursorPos);

      const newValue = beforeFilter + insertText + afterCursor;
      commitUserEdit(stringToSegments(newValue));

      // Position cursor appropriately
      if (insertText.endsWith("('')")) {
        // For functions with string arg, position between quotes
        newCursorOffset = beforeFilter.length + insertText.length - 2;
      } else if (insertText.endsWith('()')) {
        // For functions, position after the closing paren
        newCursorOffset = beforeFilter.length + insertText.length;
      } else {
        newCursorOffset = beforeFilter.length + insertText.length;
      }
    } else {
      // At the start of expression - replace the current content
      const exprStart = context.expressionStart;
      const beforeExpr = textValue.substring(0, exprStart + 2); // Include ${
      const afterCursor = textValue.substring(cursorPos);

      const newValue = beforeExpr + insertText + afterCursor;
      commitUserEdit(stringToSegments(newValue));

      if (insertText.endsWith("('')")) {
        newCursorOffset = beforeExpr.length + insertText.length - 2;
      } else if (insertText.endsWith('()')) {
        newCursorOffset = beforeExpr.length + insertText.length;
      } else {
        newCursorOffset = beforeExpr.length + insertText.length;
      }
    }

    setShowAutocomplete(false);
    isInteractingWithAutocompleteRef.current = false;
    autocompleteContextRef.current = null;

    // Restore focus and cursor position
    requestAnimationFrame(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        restoreCursorPosition(newCursorOffset);
      }
    });
  }, [textValue, commitUserEdit, restoreCursorPosition]);

  /**
   * Close autocomplete
   */
  const closeAutocomplete = useCallback(() => {
    setShowAutocomplete(false);
    isInteractingWithAutocompleteRef.current = false;
  }, []);

  // Handle text changes
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;

    // innerText reflects what the user sees (preserves newlines from <br>/<div>),
    // but it is undefined in jsdom and can be transiently empty in some browser
    // states. Fall back to textContent so we never lose dynamic content.
    let newText = editorRef.current.innerText ?? editorRef.current.textContent ?? '';

    // Browsers often leave behind whitespace/newlines when all content is deleted
    // Treat whitespace-only content as empty
    if (newText.trim() === '') {
      newText = '';
      // Clear the editor to ensure :empty CSS selector works
      editorRef.current.textContent = '';
    }

    commitUserEdit(stringToSegments(newText));
    // Update autocomplete after a small delay to let the DOM update
    requestAnimationFrame(() => {
      updateAutocomplete();
    });
  }, [commitUserEdit, updateAutocomplete]);
  /**
   * Check if there's an unmatched opening bracket before the cursor.
   * Used to determine if we should skip over a closing bracket.
   */
  const hasUnmatchedOpening = useCallback((text: string, cursorPos: number, openChar: string, closeChar: string): boolean => {
    let depth = 0;
    // For quotes, we just check if we're inside a string (odd number of quotes before cursor)
    if (openChar === closeChar) {
      // Count quotes before cursor, ignoring escaped ones
      let count = 0;
      for (let i = 0; i < cursorPos; i++) {
        if (text.charAt(i) === openChar && (i === 0 || text.charAt(i - 1) !== '\\')) {
          count++;
        }
      }
      return count % 2 === 1; // Odd number means we're inside a string
    }

    // For brackets, count depth
    for (let i = 0; i < cursorPos; i++) {
      const char = text.charAt(i);
      if (char === openChar) depth++;
      else if (char === closeChar) depth--;
    }
    return depth > 0;
  }, []);

  // Handle keydown for autocomplete navigation and auto-pairing
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Internal undo/redo — must run before any other handling so the
    // user's text is restored instead of the global handler firing.
    // We stopPropagation so the document-level toolbar shortcut (which
    // already bails on contentEditable via shouldSkipShortcut, but
    // defensively) cannot also act on this event. Skip during IME
    // composition — Ctrl+Z there is the browser's "cancel composition"
    // and must not be hijacked. `isPrimaryMod` gives us ⌘ on Mac and
    // Ctrl on Win/Linux so the field-local history matches platform
    // expectations.
    if (isPrimaryMod(e) && !e.altKey && !e.nativeEvent.isComposing) {
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'z' && e.shiftKey) || key === 'y';
      if (isUndo) {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        performUndo();
        return;
      }
      if (isRedo) {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        performRedo();
        return;
      }
    }

    // Auto-pair characters: quotes and brackets
    // Map of opening to closing characters
    const pairMap: Record<string, string> = {
      "'": "'",
      '"': '"',
      '`': '`',
      '(': ')',
      '[': ']',
      '{': '}',
    };
    // Reverse map: closing to opening
    const reversePairMap: Record<string, string> = {
      "'": "'",
      '"': '"',
      '`': '`',
      ')': '(',
      ']': '[',
      '}': '{',
    };
    // Set of closing characters (for skip-over behavior)
    const closingChars = new Set(["'", '"', '`', ')', ']', '}']);

    // Skip-over: when typing a closing character, skip if:
    // 1. The character after cursor is the same closing character
    // 2. There's an unmatched opening character before cursor
    // This uses bracket matching instead of position tracking for robustness
    if (closingChars.has(e.key) && !e.ctrlKey && !e.metaKey) {
      const cursorPos = saveCursorPosition();
      if (cursorPos !== null && editorRef.current) {
        const text = editorRef.current.innerText || '';
        const charAfterCursor = text.charAt(cursorPos);

        if (charAfterCursor === e.key) {
          const openChar = reversePairMap[e.key];
          // Check if there's an unmatched opening bracket
          if (openChar && hasUnmatchedOpening(text, cursorPos, openChar, e.key)) {
            e.preventDefault();
            // Just move cursor forward one position
            restoreCursorPosition(cursorPos + 1);
            return;
          }
        }
      }
    }

    // Handle Enter key - manually insert newline for consistent cross-browser behavior
    // Different browsers handle Enter differently (some use <br>, some use <div>)
    // By handling it ourselves, we ensure consistent cursor positioning after re-render
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !showAutocomplete) {
      e.preventDefault();
      const cursorPos = saveCursorPosition();
      if (cursorPos !== null) {
        // Insert newline at cursor position
        const before = textValue.slice(0, cursorPos);
        const after = textValue.slice(cursorPos);
        const newValue = before + '\n' + after;
        commitUserEdit(stringToSegments(newValue));

        // Position cursor after the newline
        requestAnimationFrame(() => {
          restoreCursorPosition(cursorPos + 1);
        });
      }
      return;
    }

    const openingChar = e.key;
    const closingChar = pairMap[openingChar];

    // Auto-pair: when typing an opening character, insert both and place cursor between
    if (closingChar && !e.ctrlKey && !e.metaKey) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editorRef.current) {
        const range = selection.getRangeAt(0);

        // Check if typing { after $ to trigger autocomplete
        const shouldTriggerAutocomplete = openingChar === '{' && !showAutocomplete;
        const cursorPosBeforeInsert = saveCursorPosition();
        const charBeforeCursor = cursorPosBeforeInsert !== null && cursorPosBeforeInsert > 0
          ? textValue.charAt(cursorPosBeforeInsert - 1)
          : '';

        // For quotes, don't auto-pair if there's a non-whitespace character before cursor
        // This handles cases like: don't, "hello", etc. where the quote ends a word/string
        const isQuote = openingChar === "'" || openingChar === '"' || openingChar === '`';
        const hasNonWhitespaceBefore = charBeforeCursor && !/\s/.test(charBeforeCursor);
        if (isQuote && hasNonWhitespaceBefore && range.collapsed) {
          // Don't auto-pair, let the browser handle it normally
          return;
        }

        e.preventDefault();

        // Check if we're inside a selection (text is selected)
        if (!range.collapsed) {
          // Wrap selected text in the pair
          const selectedText = range.toString();
          range.deleteContents();
          const textNode = document.createTextNode(openingChar + selectedText + closingChar);
          range.insertNode(textNode);
          // Position cursor after closing character
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // No selection - insert pair and position cursor between
          const pairNode = document.createTextNode(openingChar + closingChar);
          range.insertNode(pairNode);
          // Position cursor between the pair
          range.setStart(pairNode, 1);
          range.setEnd(pairNode, 1);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        handleInput();

        // Trigger autocomplete for ${ pattern
        if (shouldTriggerAutocomplete && charBeforeCursor === '$') {
          requestAnimationFrame(() => {
            updateAutocomplete();
          });
        }
        return;
      }
    }

    // Handle backspace - delete matching pair if adjacent
    if (e.key === 'Backspace' && !e.ctrlKey && !e.metaKey) {
      const cursorPos = saveCursorPosition();
      if (cursorPos !== null && cursorPos > 0 && editorRef.current) {
        const text = editorRef.current.innerText || '';
        const charBefore = text.charAt(cursorPos - 1);
        const charAfter = text.charAt(cursorPos);

        // Check if cursor is between matching pair (quotes or brackets)
        const expectedClosing = pairMap[charBefore];
        if (expectedClosing && charAfter === expectedClosing) {
          e.preventDefault();
          // Delete both characters
          const newText = text.slice(0, cursorPos - 1) + text.slice(cursorPos + 1);
          commitUserEdit(stringToSegments(newText));
          // Restore cursor position (one position back)
          requestAnimationFrame(() => {
            restoreCursorPosition(cursorPos - 1);
          });
          return;
        }
      }
    }

    // Check for autocomplete trigger
    if (e.key === '.' && !showAutocomplete) {
      // Dot typed - show autocomplete after DOM updates
      requestAnimationFrame(() => {
        updateAutocomplete();
      });
      return;
    }

    // If autocomplete is not showing, nothing else to do
    // (Note: ${ trigger is handled in the auto-pair section above)
    if (!showAutocomplete) {
      return;
    }

    // Autocomplete is showing - handle navigation
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (autocompleteSuggestions[selectedSuggestionIndex]) {
          handleAutocompleteSelect(autocompleteSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeAutocomplete();
        break;
    }
  }, [showAutocomplete, saveCursorPosition, hasUnmatchedOpening, restoreCursorPosition, textValue, commitUserEdit, handleInput, updateAutocomplete, autocompleteSuggestions, selectedSuggestionIndex, closeAutocomplete, handleAutocompleteSelect, performUndo, performRedo]);

  // Handle blur - save cursor position before losing focus
  const handleBlur = useCallback(() => {
    // Save cursor position before blur (for menu insertions)
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      if (editorRef.current.contains(range.startContainer)) {
        const preCaretRange = document.createRange();
        preCaretRange.selectNodeContents(editorRef.current);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        lastCursorPositionRef.current = preCaretRange.toString().length;
      }
    }
    // Don't close or update if interacting with autocomplete
    if (!isInteractingWithAutocompleteRef.current) {
      setIsFocused(false);
      closeAutocomplete();
      // Only capture final value when not interacting with autocomplete
      // BUT: Don't capture if the content is just the placeholder text
      // This prevents the placeholder from becoming the value when clicking/blurring an empty field
      if (editorRef.current) {
        const currentContent = editorRef.current.innerText;
        const placeholderText = placeholder ?? 'Enter value...';
        // Only call handleInput if content is not the placeholder
        if (currentContent !== placeholderText) {
          handleInput();
        }
      }
    }
  }, [handleInput, closeAutocomplete, placeholder]);

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Handle paste - strip formatting and insert plain text
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    // Keep DOM text and caret offsets on LF before rendering line breaks.
    const text = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');

    // Use Selection API to insert text at cursor position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      // Move cursor to end of inserted text
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (editorRef.current) {
      // Fallback: append to end
      editorRef.current.textContent = (editorRef.current.textContent ?? '') + text;
    }

    // Trigger change
    handleInput();
  }, [handleInput]);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  // Handle drag leave
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget === e.target || !editorRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  /**
   * Get the character offset at a specific point in the editor.
   * Used for drop positioning.
   */
  const getOffsetAtPoint = useCallback((x: number, y: number): number => {
    if (!editorRef.current) return textValue.length;

    // Use caretPositionFromPoint (Firefox) or caretRangeFromPoint (Chrome/Safari)
    let range: Range | null = null;

    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(x, y);
    } else if ((document as Document & { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } }).caretPositionFromPoint) {
      const pos = (document as Document & { caretPositionFromPoint: (x: number, y: number) => { offsetNode: Node; offset: number } }).caretPositionFromPoint(x, y);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }

    if (!range || !editorRef.current.contains(range.startContainer)) {
      // Fallback: insert at end
      return textValue.length;
    }

    // Calculate character offset using the same method as saveCursorPosition
    // This properly counts <br> elements as newlines
    const countOffset = (node: Node, targetNode: Node, targetOffset: number): number | null => {
      if (node === targetNode) {
        if (node.nodeType === Node.TEXT_NODE) {
          return targetOffset;
        }
        // For element nodes, targetOffset is the child index
        let count = 0;
        const children = Array.from(node.childNodes);
        for (let i = 0; i < targetOffset && i < children.length; i++) {
          const child = children[i];
          if (child) {
            count += getNodeTextLength(child);
          }
        }
        return count;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        return null;
      }

      if (node.nodeName === 'BR') {
        return null;
      }

      // Traverse children
      let offset = 0;
      for (const child of Array.from(node.childNodes)) {
        if (child.contains(targetNode) || child === targetNode) {
          const childOffset = countOffset(child, targetNode, targetOffset);
          if (childOffset !== null) {
            return offset + childOffset;
          }
        }
        offset += getNodeTextLength(child);
      }

      return null;
    };

    const result = countOffset(editorRef.current, range.startContainer, range.startOffset);
    return result ?? textValue.length;
  }, [textValue.length, getNodeTextLength]);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      // Read-only / disabled inputs ignore drops outright.
      if (disabled || effectiveReadonly) return;

      // Get drop position
      const dropOffset = getOffsetAtPoint(e.clientX, e.clientY);

      // Check if we're dropping inside an existing ${...} expression
      const context = analyzeExpressionContext(textValue, dropOffset);
      const isInsideExpression = context.isInExpression;

      // Try to get token data from JSON
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        try {
          const token = applyTokenTransforms(JSON.parse(jsonData) as Token);

          // Generate the token expression text
          // If dropping inside an existing expression, don't wrap in ${...}
          let tokenText: string;
          if (typeof token.value === 'string') {
            // Token has a string value - use it, but strip ${...} wrapper if inside expression
            if (isInsideExpression && token.value.startsWith('${') && token.value.endsWith('}')) {
              // Strip the ${...} wrapper
              tokenText = token.value.slice(2, -1);
            } else {
              tokenText = token.value;
            }
          } else {
            // Build the bare token-as-function call, then wrap in ${...}
            // only when we're outside an existing expression. Inside one,
            // the surrounding wrapper already provides the expression
            // context so we paste the bare call.
            const fnCall = `${token.tokenType}('${token.name ?? token.title}')`;
            tokenText = isInsideExpression ? fnCall : wrapAsExpression(fnCall);
          }

          // Insert at drop position instead of appending
          const before = textValue.slice(0, dropOffset);
          const after = textValue.slice(dropOffset);
          const newValue = before + tokenText + after;

          commitUserEdit(stringToSegments(newValue));

          // Set cursor position after the inserted token
          requestAnimationFrame(() => {
            if (editorRef.current) {
              editorRef.current.focus();
              const newCursorPos = dropOffset + tokenText.length;
              restoreCursorPosition(newCursorPos);
            }
          });
          return;
        } catch {
          // Fall back to plain text
        }
      }

      // Fall back to plain text. If the drag originated from the
      // function picker (`application/x-otto-expression` MIME) the
      // payload is a workflow-expression template — wrap it in ${...}
      // when the drop position is *outside* an existing expression so it
      // evaluates as an expression at runtime instead of becoming a
      // literal string. Inside an existing ${...} we paste the bare
      // template (the surrounding wrapper already provides the
      // expression context).
      const text = e.dataTransfer.getData('text/plain');
      if (text && editorRef.current) {
        const expressionTemplate = e.dataTransfer.getData(
          'application/x-otto-expression',
        );
        const wrappedText =
          expressionTemplate && !isInsideExpression
            ? wrapAsExpression(expressionTemplate)
            : text;

        // Insert at drop position
        const before = textValue.slice(0, dropOffset);
        const after = textValue.slice(dropOffset);
        const newValue = before + wrappedText + after;
        commitUserEdit(stringToSegments(newValue));

        // Set cursor position after the inserted text
        requestAnimationFrame(() => {
          if (editorRef.current) {
            editorRef.current.focus();
            const newCursorPos = dropOffset + wrappedText.length;
            restoreCursorPosition(newCursorPos);
          }
        });
      }
    },
    [textValue, commitUserEdit, getOffsetAtPoint, restoreCursorPosition, disabled, effectiveReadonly, applyTokenTransforms]
  );

  // Render highlighted content with optional cursor preservation
  const renderHighlightedContent = useCallback((preserveCursor: boolean = false) => {
    if (!editorRef.current) return;

    // Save cursor position if needed
    const cursorOffset = preserveCursor ? saveCursorPosition() : null;

    if (hasExpressions && parsedSegments.length > 0) {
      const expressionClass = styles.expression;
      const htmlParts: string[] = [];

      for (const seg of parsedSegments) {
        if (seg.isExpression) {
          htmlParts.push(`<span class="${expressionClass}">${escapeHtml(seg.text)}</span>`);
        } else {
          htmlParts.push(escapeHtml(seg.text));
        }
      }

      editorRef.current.innerHTML = htmlParts.join('');
    } else {
      // Use innerHTML with escaped content to properly render newlines as <br>
      editorRef.current.innerHTML = escapeHtml(textValue);
    }

    // Restore cursor position if we saved it
    if (preserveCursor && cursorOffset !== null) {
      restoreCursorPosition(cursorOffset);
    }
  }, [hasExpressions, parsedSegments, textValue, styles.expression, saveCursorPosition, restoreCursorPosition]);

  // Track the last text value we rendered to avoid unnecessary re-renders
  const lastRenderedValueRef = useRef<string>('');

  // Update editor content when value changes
  useEffect(() => {
    if (!editorRef.current) return;

    // When empty, clear the editor content so CSS :empty pseudo-class works for placeholder
    if (isEmpty) {
      // Only clear if there's actual content (avoid unnecessary DOM updates)
      if (editorRef.current.textContent !== '') {
        editorRef.current.textContent = '';
      }
      lastRenderedValueRef.current = '';
      return;
    }

    // Always re-render with syntax highlighting
    // When focused, preserve cursor position; when not focused, no need to preserve
    renderHighlightedContent(isFocused);
    lastRenderedValueRef.current = textValue;
  }, [textValue, isFocused, isEmpty, renderHighlightedContent]);

  // Close autocomplete when clicking outside the container
  useEffect(() => {
    if (!isFocused) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Don't close if clicking inside the container (editor + autocomplete)
      if (containerRef.current?.contains(e.target as Node)) {
        return;
      }
      // Don't close if currently interacting with autocomplete
      if (isInteractingWithAutocompleteRef.current) {
        return;
      }
      setIsFocused(false);
      closeAutocomplete();
    };

    // Use mousedown to catch the click before focus changes
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFocused, closeAutocomplete]);

  return (
    <div
      ref={containerRef}
      className={mergeClasses(styles.container, fillParent && styles.containerFill)}
    >
      <div
        className={mergeClasses(
          styles.inputWrapper,
          fillParent && styles.inputWrapperFill
        )}
      >
        <div
          ref={editorRef}
          className={mergeClasses(
            styles.editor,
            multiline && styles.editorMultiline,
            fillParent && styles.editorFill,
            isFocused && styles.editorFocused,
            isDragOver && styles.editorDropTarget,
            disabled && styles.editorDisabled,
            styles.placeholder
          )}
          style={
            // Only apply cap in multiline mode. Values are kept off the
            // Griffel makeStyles path because they're consumer-supplied
            // (can be any valid CSS length or clamp() expression) — inline
            // styles keep the API dynamic without exploding the class cache.
            multiline && maxHeight !== undefined
              ? { maxHeight, overflowY: 'auto' }
              : undefined
          }
          contentEditable={!disabled && !effectiveReadonly}
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="textbox"
          aria-label={ariaLabel ?? placeholder}
          aria-multiline={multiline}
          aria-disabled={disabled}
          aria-readonly={effectiveReadonly}
          aria-placeholder={placeholder}
          data-placeholder={placeholder || 'Enter value...'}
        />
      </div>

      {/* Autocomplete dropdown */}
      {showAutocomplete && autocompleteSuggestions.length > 0 && (
        <ExpressionAutocomplete
          position={autocompletePosition}
          suggestions={autocompleteSuggestions}
          onSelect={handleAutocompleteSelect}
          onClose={closeAutocomplete}
          selectedIndex={selectedSuggestionIndex}
          onSelectedIndexChange={setSelectedSuggestionIndex}
          filterText={autocompleteFilterText}
          headerText={autocompleteContext?.isAfterDot
            ? `${autocompleteInferredType} methods`
            : 'Functions'}
          onMouseDown={() => {
            isInteractingWithAutocompleteRef.current = true;
          }}
        />
      )}

    </div>
  );
};

/**
 * Escape HTML entities and convert newlines to <br> for contentEditable
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    // A leftover CR would be parsed as another newline alongside the <br>.
    .replace(/\r\n?|\n/g, '<br>');
}

TokenizedInput.displayName = 'TokenizedInput';
